import { Component, ElementRef, Inject, OnInit, Type, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { WizardPlugin } from '../../../../../decorators/wizard-plugin';
import { CustomerAllocationData } from '../../../../../models/wizard-plugin-models/customer-allocation-data';
import { IWizardPluginComponent } from '../../../../../interfaces/wizard-plugin-component.interface';
import { DecoratorConfig } from '../../../../../decoration-base/decorator-config';
import { IPluginPreviewComponent } from '../../../../../interfaces/plugin-preview-component.interface';
import { BasePluginData } from '../../../../../models/wizard-plugin-models/base-plugin-data';
import { RequisitionData } from '../../../../../models/wizard-plugin-models/requisition-data';
import { CustomerEntity } from '../../../../../models/entities/customer';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ResourceCreatorService } from '../../../../../services/resource-creator.service';
import { ErrorHandlerService } from '../../../../../decoration-base/error-handler.service';
import { COURSE_ENTITY_SERVICE_TOKEN, CLASSES_ENTITY_SERVICE_TOKEN, CUSTOMER_ENTITY_SERVICE_TOKEN, TRAINER_ENTITY_SERVICE_TOKEN, REQUISITION_ENTITY_SERVICE_TOKEN } from '../../../../../injection-tokens/entity-service-tokens';
import { IClassesEntityService } from '../../../../../interfaces/entities/classes-entity-service.interface';
import { ICourseEntityService } from '../../../../../interfaces/entities/course-entity-service.interface';
import { ICustomerEntityService } from '../../../../../interfaces/entities/customer-entity-service.interface';
import { IRequisitionEntityService } from '../../../../../interfaces/entities/requisition-entity-service-interface';
import { ITrainerEntityService } from '../../../../../interfaces/entities/trainer-entity-service.interface';
import { RequisitionPreviewComponent } from './requisition-preview/requisition-preview.component';
import { CustomerEnquiryForm } from '../../../../../models/home/customer-enquiry-form';
import { LoginEntityService } from '../../../../../services/entity-services/login-entity.service';
import { EnquiryConversation } from '../../../../../models/home/enquiry-conversation';
import { EnquiryAssigned } from '../../../../../models/home/enquiry-assigned';
import { EnquiryState } from '../../../../../models/home/enquiry-state';
import { EnquiryEntityService } from '../../../../../services/enquiry-services/enquiry-entity.service';

import { Log, IShortLogger } from '../../../../../services/logger.service';
@WizardPlugin({
  name: "Requisition Creation",
  optional: false,
  description: "Create a requisition for customers",
  dataModelType: RequisitionData
})


@Component({
  selector: 'app-requisition-plugin',
  templateUrl: './requisition-plugin.component.html',
  styleUrl: './requisition-plugin.component.css'
})
export class RequisitionPluginComponent implements IWizardPluginComponent, OnInit {
  private readonly logger: IShortLogger<RequisitionPluginComponent> = Log.create(RequisitionPluginComponent);


  paymentTermsOptions = [{ term: 'Advance Payment' }, { term: 'Net 15 Days' },
  { term: 'Net 30 Days' }, { term: 'Net 45 Days' }, { term: 'Net 60 Days' }];

  /** Currently selected customer shown as chip */
  selectedCustomer: CustomerEntity | null = null;

  /** Blank model for the new-customer drawer form */
  newCustomer: CustomerEntity = new CustomerEntity();

  /** Controls visibility of the fixed new-customer side panel */
  customerDrawerOpen = false;

  openCustomerDrawer(): void {
    this.newCustomer = new CustomerEntity();
    this.customerDrawerOpen = true;
  }

  closeCustomerDrawer(): void {
    this.customerDrawerOpen = false;
  }

  saveNewCustomer(): void {
    if (!this.newCustomer.name) return;
    this.customerService.createCustomer(this.newCustomer, '').subscribe({
      next: (saved: any) => {
        const entity = saved as CustomerEntity;
        this.customers.push(entity);
        this.filteredCustomers = this.customers.slice(0, 20);
        this.selectCustomerEntity(entity);
        this.customerDrawerOpen = false;
        this.snackBar.open(`Customer "${entity.name}" added.`, 'OK', { duration: 2500 });
      },
      error: () => {
        this.snackBar.open('Failed to save customer. Please try again.', 'Dismiss', { duration: 3000 });
      }
    });
  }

  private selectCustomerEntity(c: CustomerEntity): void {
    this.selectedCustomer = c;
    this.customerSearch = c.name;
    this.updateCustomerSelections({ value: c.id });
  }

  showCustomerPopup: any;
  async onCloseCustomerPopup() {
    this.showCustomerPopup = false;
  }
  openAddCustomerModal() {
    this.showCustomerPopup = true;
  }
  public pluginData: RequisitionData = new RequisitionData();
  public customers: CustomerEntity[] = [];
  statusOptions = [{ 'status': 'Draft' }, { 'status': 'Submitted' }, { 'status': 'In Review' }, { 'status': 'Approved' }, { 'status': 'Rejected' }, { 'status': 'Completed' }];

  customerTypes = [{ 'type': 'Corporate' }, { 'type': 'Individual' }, { 'type': 'Educational Institution' }, { 'type': 'Government' }, { 'type': 'Non-profit' }];
  readonly customerTypeValues = ['Corporate', 'Individual', 'Educational Institution', 'Government', 'Non-profit'];
  filteredCustomerTypes: string[] = [...['Corporate', 'Individual', 'Educational Institution', 'Government', 'Non-profit']];
  customerTypeSearch: string = '';

  filterCustomerTypes(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredCustomerTypes = q
      ? this.customerTypeValues.filter(t => t.toLowerCase().includes(q))
      : [...this.customerTypeValues];
  }

  onCustomerTypeSelected(type: string): void {
    this.pluginData.customerDetails.customerType = type;
    this.customerTypeSearch = type;
  }

  filteredCustomers: CustomerEntity[] = [];
  customerSearch: string = '';

  filterCustomers(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredCustomers = q
      ? this.customers.filter(c => c.name.toLowerCase().includes(q))
      : this.customers.slice(0, 20);
  }

  clearCustomer(): void {
    this.selectedCustomer = null;
    this.customerSearch = '';
    this.filteredCustomers = this.customers.slice(0, 20);
    this.pluginData.customerDetails.id = '';
    this.pluginData.customerDetails.name = '';
  }

  displayCustomer = (c: CustomerEntity | null): string => c ? c.name : '';

  onCustomerSelected(c: CustomerEntity): void {
    this.selectCustomerEntity(c);
  }

  constructor(private fb: FormBuilder,
    private router: Router,
    @Inject(CUSTOMER_ENTITY_SERVICE_TOKEN) private customerService: ICustomerEntityService,
    @Inject(REQUISITION_ENTITY_SERVICE_TOKEN) private reqService: IRequisitionEntityService,
    private errorHandler: ErrorHandlerService,
    private route: ActivatedRoute,
    private loginEntityService: LoginEntityService,
    private enquiryEntiryService: EnquiryEntityService,
    private snackBar: MatSnackBar
  ) {
    // Don't call createForm here - FormBuilder might not be ready
  }

  enquiryId: string = '';
  enabledOrDisabled: string = '';
  enquiries: CustomerEnquiryForm[] = [];
  isEnquiryEnabled: boolean = true;


  get docDateString(): string {
    if (!this.pluginData.docDate) return '';
    const d = new Date(this.pluginData.docDate);
    return d.toISOString().substring(0, 10);
  }

  set docDateString(val: string) {
    this.pluginData.docDate = val ? new Date(val) : new Date();
  }

  async ngOnInit(): Promise<void> {
    this.route.paramMap.subscribe(params => {
      this.enquiryId = params.get('enquiryId') || '';
      this.enabledOrDisabled = params.get('enabledOrDisabled') || '';

      this.logger.i('Enquiry ID from route:', this.enabledOrDisabled);

      if (this.enabledOrDisabled == "disabled") {
        this.isEnquiryEnabled = false;
      }

    });
    // this.loadCustomerEnquiries();
    this.initializePluginData();
    await this.loadCustomers();
    this.filteredCustomers = this.customers.slice(0, 20);

  }

  formatEnquiry = (data: any) => {
    if (!data) return '';
    return `${data.id} - ${data.name}-${data.contactPerson}`; // or whatever fields you want
  };

  async loadCustomerEnquiries(): Promise<void> {
    const rawEnquiries = await this.enquiryEntiryService.getAllCustomerEnquiries();

    // Convert plain objects to CustomerEnquiryForm instances
    this.enquiries = rawEnquiries.map((enquiry: { id: string; name: string; contactPerson: string; email: string; mobileNumber: string | undefined; subject: string; enquiryDetails: string; createdDate: Date | undefined; conversations: EnquiryConversation[] | undefined; assignedHistory: EnquiryAssigned[] | undefined; stateHistory: EnquiryState[] | undefined; }) => {
      const customerEnquiry = new CustomerEnquiryForm();

      // Manually assign only the data properties (not getters)
      customerEnquiry.id = enquiry.id;
      customerEnquiry.name = enquiry.name;
      customerEnquiry.contactPerson = enquiry.contactPerson;
      customerEnquiry.email = enquiry.email;
      customerEnquiry.mobileNumber = enquiry.mobileNumber;
      customerEnquiry.subject = enquiry.subject;
      customerEnquiry.enquiryDetails = enquiry.enquiryDetails;
      customerEnquiry.createdDate = enquiry.createdDate;
      customerEnquiry.conversations = enquiry.conversations;
      customerEnquiry.assignedHistory = enquiry.assignedHistory;
      customerEnquiry.stateHistory = enquiry.stateHistory;

      return customerEnquiry;
    });
    // ✅ Set pluginData.enquiryId AFTER enquiries are loaded
    if (!this.pluginData.enquiryId && this.enquiryId) {
      this.pluginData.enquiryId = this.enquiryId;
    }
    this.logger.i('Loaded Customer Enquiries:', this.enquiries);
  }

  private initializePluginData(): void {
    // Set default values if not already set
    if (!this.pluginData.docId) this.pluginData.docId = '0';
    if (!this.pluginData.docDate)
      this.pluginData.docDate = new Date();
    if (!this.pluginData.docNo) this.pluginData.docNo = '0';
    if (!this.pluginData.status) this.pluginData.status = 'Draft';
    if (!this.pluginData.enquiryId) this.pluginData.enquiryId = this.enquiryId;
    if (!this.pluginData.requisitionType) this.pluginData.requisitionType = 'labOnly'; // Default type
  }

  async loadCustomers(): Promise<void> {
    this.errorHandler.tryExecuteAsync(async () => {
      this.customers = await this.customerService.getAllCustomers();
    }, 'loadCustomers');
    this.logger.i('Loaded customers:', this.customers);
  }

  getPluginData(): BasePluginData {
    return this.pluginData;
  }

  setPluginData(data: BasePluginData): void {
    if (data) {
      this.pluginData = data as RequisitionData;
      // restore autocomplete search fields from saved data
      if (this.pluginData.customerDetails?.id) {
        const found = this.customers.find(c => c.id === this.pluginData.customerDetails.id);
        this.selectedCustomer = found ?? null;
        this.customerSearch = found?.name ?? '';
      }
      if (this.pluginData.customerDetails?.customerType) {
        this.customerTypeSearch = this.pluginData.customerDetails.customerType;
      }
    }
  }


  validate(): boolean {
    return !!this.pluginData.enquiryId
      && !!this.pluginData.customerDetails.customerType
      && !!this.pluginData.customerDetails.id
      && !this.isDuplicateEnquiryId;
  }
  isComplete(): boolean {
    return this.validate();
  }
  reset(): void {
    this.pluginData = new RequisitionData();
  }

  asPreview?(): Type<IPluginPreviewComponent> {
    return RequisitionPreviewComponent
  }

  updateCustomerSelections(e: any): void {
    try {
      this.pluginData.customerDetails.id = e.value;

      const selectedCustomer = this.customers.find(customer => customer.id === this.pluginData.customerDetails.id); //this.customerService.getCustomerById(this.pluginData.customerDetails.id);
      if (selectedCustomer) {
        this.pluginData.customerDetails.name = selectedCustomer.name || '';

        this.pluginData.customerDetails.contactPerson.name = selectedCustomer.contactPerson.name;
        this.pluginData.customerDetails.contactPerson.mobile = selectedCustomer.contactPerson.mobile;
        this.pluginData.customerDetails.contactPerson.email = selectedCustomer.contactPerson.email;
        this.pluginData.customerDetails.address = selectedCustomer.address || '';
        this.pluginData.customerDetails.gstNumber = selectedCustomer.gstNumber || '';
      }
    } catch (error) {
      this.logger.e('Error updating Customer selections:', error);
    }
  }


  async refreshList(savedCustomer: any) {
    this.logger.i('Saved Customer:', savedCustomer);
    await this.loadCustomers();

    this.pluginData.customerDetails.id = savedCustomer.id;

    this.pluginData.customerDetails.name = savedCustomer.name || '';

    this.pluginData.customerDetails.contactPerson.name = savedCustomer.contactPerson.name;
    this.pluginData.customerDetails.contactPerson.mobile = savedCustomer.contactPerson.mobile;
    this.pluginData.customerDetails.contactPerson.email = savedCustomer.contactPerson.email;
    this.pluginData.customerDetails.address = savedCustomer.address || '';
    this.pluginData.customerDetails.gstNumber = savedCustomer.gstNumber || '';
    // this.loadRequisitions(); // re-fetch list
  }

  @ViewChild('enquiryIdInput') enquiryIdInput!: ElementRef<HTMLInputElement>;
  isDuplicateEnquiryId: boolean = false;

  async onEnquiryIdBlur(): Promise<void> {

    if (!this.pluginData.enquiryId) return;

    try {
      const isDuplicate = await this.reqService.checkDuplicateEnquiry(this.pluginData.enquiryId);


      this.isDuplicateEnquiryId = isDuplicate.exists; // <-- set flag

      if (this.isDuplicateEnquiryId) {
        this.snackBar.open(`Enquiry No "${this.pluginData.enquiryId}" already exists.`, 'Dismiss', { duration: 3000 });
        this.pluginData.enquiryId = '';
        setTimeout(() => { this.enquiryIdInput.nativeElement.focus(); }, 0);
      }
    } catch (error) {
      this.logger.e('Error checking enquiryId:', error);
      this.snackBar.open('Error while checking Enquiry No.', 'Dismiss', { duration: 3000 });
      this.isDuplicateEnquiryId = false;
    }
  }

}
